import type { FC } from "react"
import { useState } from "react"
import Stories from "./capsules/stories/Stories"
import classNames from "classnames"
import { GrowthBookExperiment } from "~/providers/GrowthBookProvider"
import type { WithStoriesFragment } from "~/contentful"
import type { SerializeFrom } from "@remix-run/node"

interface Props {
  stories: SerializeFrom<WithStoriesFragment[]>
}

const StoriesGroup: FC<Props> = ({ stories }) => {
  const [selectedStory, setSelectedStory] = useState<number | null>(null)

  const [currentStoryItem, setCurrentStoryItem] = useState<number>(0)

  const hasStories = stories.length > 0

  if (!hasStories) return null

  const handleStoryEnd = (index: number) => {
    if (index < stories?.length - 1) {
      setSelectedStory(index + 1)
    } else {
      setSelectedStory(null)
    }
  }

  const handleStoryPrevious = (index: number) => {
    if (index === 0 && !currentStoryItem) {
      setSelectedStory(null)
    } else if (currentStoryItem) {
      setCurrentStoryItem(currentStoryItem - 1)
    } else {
      setSelectedStory(index - 1)
      setCurrentStoryItem(0)
    }
  }

  const handleStoryNext = (index: number, storyLength: number) => {
    if (currentStoryItem === storyLength - 1) {
      setSelectedStory(index + 1)
      setCurrentStoryItem(0)
    } else {
      setCurrentStoryItem(currentStoryItem + 1)
    }
  }

  const handleThumbnailClick = (index: number) => {
    setSelectedStory(index)
    setCurrentStoryItem(0)
  }

  return (
    <div
      className={classNames(
        "flex animate-[slideIn_1s_ease_forwards] items-start overflow-x-scroll scrollbar-none",
        stories.length <= 3 ? "justify-center" : "ml-4"
      )}
    >
      {stories.map((story, index) => {
        if (!story) return null
        if (!story.experimentId || !story.experimentVariation) {
          return (
            <Stories
              key={story.sys.id}
              {...story}
              thumbnail={story.thumbnail?.url}
              index={index}
              currentItem={currentStoryItem}
              hasNext={index < stories.length - 1}
              onThumbnailClick={() => handleThumbnailClick(index)}
              onStoryEnd={() => handleStoryEnd(index)}
              onPrevious={() => handleStoryPrevious(index)}
              onNext={() =>
                handleStoryNext(
                  index,
                  story?.itemsCollection?.items.length || 0
                )
              }
              open={selectedStory === index}
              onClose={() => setSelectedStory(null)}
            />
          )
        }
        return (
          <GrowthBookExperiment
            key={story.sys.id}
            experiment={story.experimentId}
            variation={story.experimentVariation}
          >
            <Stories
              {...story}
              thumbnail={story.thumbnail?.url}
              index={index}
              hasNext={index < stories.length - 1}
              onThumbnailClick={() => setSelectedStory(index)}
              onStoryEnd={() => handleStoryEnd(index)}
              open={selectedStory === index}
              onClose={() => setSelectedStory(null)}
            />
          </GrowthBookExperiment>
        )
      })}
    </div>
  )
}

export default StoriesGroup
